import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Calendar,
  User as UserIcon,
  FileText,
  MessageSquare,
  Edit,
  Trash2,
  X,
  Loader2,
  ChevronDown,
  ClipboardList,
  Ticket,
  CheckCircle,
  CreditCard,
  AlertCircle,
  Video,
} from 'lucide-react';
import {
  useAppointment,
  useCancelAppointment,
  useSetVisitStatus,
  useFinishAppointment,
  useMarkNoShowAppointment,
  type VisitStatus,
} from '@/hooks/useAppointments';
import { usePatient } from '@/hooks/usePatients';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import { useServicesList } from '@/hooks/useServices';
import { useClinic } from '@/hooks/useClinicSettings';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { formatCopenhagenDate, formatCopenhagenTime } from '@/lib/clinicDateTime';
import { appointmentPaymentAmountCents, clinicVatRatePercent } from '@/lib/vat';
import { canFinishAndInvoice, finishToastKey, type FinishOutcome } from '@/lib/finishAppointment';
import { type PaymentMoreAction, type PaymentPrimaryAction } from '@/lib/appointmentPaymentPrimaryAction';
import {
  canOverrideCancellation,
  type CancellationOverrideChoice,
} from '@/lib/cancellationOverride';
import { appointmentJournalWritePath } from '@/lib/journalWritePath';
import { canStaffRescheduleAppointment } from '@/utils/appointmentEditability';
import {
  appointmentStatusLabelKey,
  canAdvanceVisitWorkflow,
  resolveAppointmentStatus,
} from '@/utils/appointmentStatus';
import {
  deriveVisitPaymentState,
  visitPaymentStateLabelKey,
} from '@/lib/appointmentPaymentState';
import {
  displayPaymentMethods,
  paymentMethodI18nKey,
  resolveAppointmentSettlement,
  settlementAllowsNewPayment,
} from '@/lib/appointmentSettlement';
import {
  buildAppointmentInvoiceHref,
  useAppointmentInvoiceLink,
} from '@/hooks/useAppointmentInvoicing';
import { PaymentDialog } from '@/components/payments/PaymentDialog';
import { InvoiceSendMenu } from '@/components/payments/InvoiceSendMenu';
import { canOfferPaymentRequest } from '@/lib/paymentRequests';
import {
  useInvoiceCreditNotes,
  useInvoicePayments,
  useSendInvoiceEmail,
  useSendInvoiceReminder,
} from '@/hooks/useInvoices';
import { usePatientActivePunchCards, useRevertPunchCardUsage } from '@/hooks/usePunchCardPayment';
import { pickPreferredPunchCard, selectApplicablePunchCards } from '@/lib/calendarPaymentChoice';
import { useStaffAppointmentPayment } from '@/hooks/useStaffAppointmentPayment';
import {
  appointmentBetalingPhase,
  betalingMore,
  betalingPrimary,
  betalingShowsInvoiceLink,
  betalingShowsSendMenu,
  betalingSummary,
  danishPaymentDate,
  type BetalingMoreAction,
} from '@/lib/appointmentBetaling';
import { AppointmentChangePaymentPanel } from '@/components/appointments/AppointmentChangePaymentPanel';
import { AppointmentCourseButton } from '@/components/appointments/AppointmentCourseButton';
import {
  occupyingResourceLabel,
  useAppointmentResourceAllocations,
} from '@/hooks/useSchedulingResources';
import { Button, ButtonGroup } from '@/components/ui/Button';
import { MenuItem, MenuPopover } from '@/components/ui/Popover';
import { Input } from '@/components/ui/Input';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { MessageModal } from './MessageModal';
import { sendStaffAppointmentEmail } from '@/lib/staffAppointmentConfirmation';
import { CancelAppointmentModal } from './CancelAppointmentModal';
import { useJoinWherebyMeeting, useSyncWherebyMeeting, useWherebyEnabled } from '@/hooks/useWhereby';
import { isVideoAppointment, openExternalMeeting } from '@/lib/whereby';

const DETAILS_TITLE = cn(TYPE.heading, 'text-ds-text');
const PATIENT_NAME = cn(TYPE.section, 'text-ds-text');
const SECTION_HEADING = cn(TYPE.section, 'uppercase tracking-wider text-ds-text');
const FIELD_LABEL = cn(TYPE.small, 'uppercase tracking-wider text-ds-muted');
const FIELD_VALUE = cn(TYPE.body, 'mt-0.5 text-ds-text');
const SECONDARY_TEXT = cn(TYPE.body, 'text-ds-text-2');
const STATUS_BADGE = TYPE.meta;

export interface AppointmentDetailsModalProps {
  appointmentId: string;
  clinicId: string;
  onClose: () => void;
  onEdit?: () => void;
}

function PreVisitStatusBadge({
  assignmentId,
  status,
}: {
  assignmentId: string | null;
  status: string | null | undefined;
}) {
  const { t } = useTranslation();
  if (!assignmentId) return null;

  const pending = !status || status === 'sent' || status === 'reminded';
  const completed = status === 'completed';
  const tone = pending
    ? 'bg-ds-amber-tint text-ds-amber-ink'
    : completed
      ? 'bg-ds-green-tint text-ds-green-ink'
      : 'bg-ds-neutral-tint text-ds-muted';
  const labelKey = pending
    ? 'appointments.details.pre_visit_pending'
    : completed
      ? 'appointments.details.pre_visit_completed'
      : 'appointments.details.pre_visit_expired';

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5',
        STATUS_BADGE,
        tone,
      )}
    >
      <ClipboardList className="h-3 w-3" />
      {t(labelKey)}
    </span>
  );
}

const VISIT_STEPS: Array<{
  key: VisitStatus;
  labelKey: string;
  activeColor: string;
  color: string;
}> = [
  {
    key: 'checked_in',
    labelKey: 'appointments.visit_status.checked_in',
    color: 'bg-ds-neutral-tint text-ds-muted',
    activeColor: 'bg-ds-teal-tint text-ds-teal',
  },
  {
    key: 'under_treatment',
    labelKey: 'appointments.visit_status.under_treatment',
    color: 'bg-ds-neutral-tint text-ds-muted',
    activeColor: 'bg-ds-coral-tint text-ds-coral-ink',
  },
  {
    key: 'treatment_finished',
    labelKey: 'appointments.visit_status.treatment_finished',
    color: 'bg-ds-neutral-tint text-ds-muted',
    activeColor: 'bg-ds-green-tint text-ds-green-ink',
  },
];

/** Top-anchored so the heading stays put when the body grows or shrinks. */
const DETAILS_SHELL =
  'flex h-full items-start justify-center px-3 pb-3 pt-[max(3rem,env(safe-area-inset-top))] md:px-4 md:pb-4 md:pt-[max(5vh,env(safe-area-inset-top))]';
const DETAILS_CARD =
  'relative flex w-full max-w-3xl max-h-[calc(100dvh-4.5rem)] flex-col overflow-hidden bg-ds-card shadow-[var(--sh-md)] md:max-h-[calc(100dvh-5vh-1.5rem)] md:rounded-2xl';

export function AppointmentDetailsModal({
  appointmentId,
  clinicId,
  onClose,
  onEdit,
}: AppointmentDetailsModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isClinicAdmin } = useAuth();

  const { data: appointment, isLoading } = useAppointment(appointmentId);
  const { data: resourceAllocations } = useAppointmentResourceAllocations(appointmentId);
  const resourceLabel = occupyingResourceLabel(resourceAllocations);
  const { data: patient } = usePatient(appointment?.patient_id ?? null);
  const { data: services } = useServicesList(clinicId);
  const { data: clinic } = useClinic(clinicId);
  const { data: wherebyEnabled } = useWherebyEnabled(clinicId);
  const joinVideo = useJoinWherebyMeeting(clinicId);
  const syncVideo = useSyncWherebyMeeting(clinicId);
  const { data: linkedInvoice } = useAppointmentInvoiceLink(appointmentId);
  const linkedInvoiceId = linkedInvoice?.kind === 'credit_note' ? null : linkedInvoice?.invoice_id;
  const { data: creditNotes } = useInvoiceCreditNotes(linkedInvoiceId);
  const { data: recordedPayments } = useInvoicePayments(linkedInvoiceId);
  const { data: activePunchCards } = usePatientActivePunchCards(appointment?.patient_id ?? null, clinicId);
  const staffPayment = useStaffAppointmentPayment();
  const sendReminder = useSendInvoiceReminder();
  const [finishChoice, setFinishChoice] = useState(false);
  const { data: punchUsage } = useQuery({
    queryKey: ['appointments', 'punch-usage', appointmentId],
    enabled: Boolean(appointmentId),
    staleTime: 15_000,
    queryFn: async (): Promise<{ id: string; punch_card_id: string } | null> => {
      const { data, error } = await supabase
        .from('punch_card_usages')
        .select('id, punch_card_id')
        .eq('appointment_id', appointmentId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const { data: journalEntry } = useQuery({
    queryKey: ['appointments', 'journal', appointmentId],
    enabled: Boolean(appointmentId),
    staleTime: 15_000,
    queryFn: async (): Promise<{ id: string; finalized_at: string | null } | null> => {
      const { data, error } = await supabase
        .from('journal_entries')
        .select('id, finalized_at')
        .eq('appointment_id', appointmentId)
        .is('discarded_at', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const cancelAppointment = useCancelAppointment();
  const setVisitStatus = useSetVisitStatus();
  const finishVisit = useFinishAppointment();
  const markNoShow = useMarkNoShowAppointment();
  const sendInvoiceEmail = useSendInvoiceEmail();
  const revertClip = useRevertPunchCardUsage();

  const canViewPunchCards = Boolean(hasPermission(clinicId, 'punch_cards', 'view'));
  const canViewInvoices = Boolean(hasPermission(clinicId, 'invoices', 'view'));
  const canCreateInvoices = Boolean(hasPermission(clinicId, 'invoices', 'create'));
  const allowOverride = canOverrideCancellation({
    isAdmin: isClinicAdmin(clinicId),
    canAmendInvoices: Boolean(hasPermission(clinicId, 'invoices', 'amend')),
    canUpdateInvoices: Boolean(hasPermission(clinicId, 'invoices', 'update')),
    canTakePayments: Boolean(hasPermission(clinicId, 'payments', 'create')),
  });

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [sendCancelEmail, setSendCancelEmail] = useState(true);
  const [showPaymentPanel, setShowPaymentPanel] = useState(false);
  const [showChangePayment, setShowChangePayment] = useState(false);
  const [overrideChoice, setOverrideChoice] = useState<CancellationOverrideChoice>('keep_policy');
  const [overrideReason, setOverrideReason] = useState('');
  const [overrideAmount, setOverrideAmount] = useState('');
  const [showLaterOverride, setShowLaterOverride] = useState(false);

  const hasPatient = Boolean(appointment?.patient_id);
  const patientName = patient ? `${patient.first_name} ${patient.last_name}`.trim() : '—';

  const preVisitAssignmentId = appointment?.pre_visit_assignment_id ?? null;

  const { data: preVisitStatus } = useQuery({
    queryKey: ['pre_visit_assignment', preVisitAssignmentId],
    enabled: Boolean(preVisitAssignmentId),
    staleTime: 30_000,
    queryFn: async (): Promise<string | null> => {
      if (!preVisitAssignmentId) return null;
      const { data } = await supabase
        .from('patient_forms_assignments')
        .select('status')
        .eq('forms_assignment_id', preVisitAssignmentId)
        .maybeSingle();
      return (data as { status: string } | null)?.status ?? null;
    },
  });

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (isLoading || !appointment) {
    return (
      <div className="fixed inset-0 z-50 overflow-hidden bg-ds-scrim">
        <div className={DETAILS_SHELL}>
          <div className={cn(DETAILS_CARD, 'rounded-2xl')}>
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-ds-muted" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const durationMinutes = Math.round(
    (new Date(appointment.ends_at).getTime() - new Date(appointment.starts_at).getTime()) / 60000,
  );
  const service = (services ?? []).find((item) => item.id === appointment.service_id);
  const priceCents = appointmentPaymentAmountCents({
    invoiceTotalCents: linkedInvoice?.total_cents,
    invoicePaidCents: linkedInvoice?.total_paid_cents,
    netPriceCents: service?.price_cents ?? 0,
    vatRatePercent: service?.vat_rate_percent ?? clinicVatRatePercent(clinic),
  });
  const lifecycleStatus = resolveAppointmentStatus({
    status: appointment.status,
    visitStatus: appointment.visit_status,
    startsAt: appointment.starts_at,
    endsAt: appointment.ends_at,
  });
  const settlement = resolveAppointmentSettlement({
    paymentStatus: appointment.payment_status,
    paymentMethod: appointment.payment_method,
    invoice:
      linkedInvoice && linkedInvoice.kind !== 'credit_note'
        ? {
            id: linkedInvoice.invoice_id,
            invoice_number: linkedInvoice.invoice_number,
            status: linkedInvoice.invoice_status,
            payment_method: linkedInvoice.payment_method,
            last_sent_at: linkedInvoice.last_sent_at,
            total_paid_cents: linkedInvoice.total_paid_cents,
          }
        : null,
    punchUsage,
  });
  const visitPayment = deriveVisitPaymentState({
    lifecycle: lifecycleStatus,
    invoiceStatus: linkedInvoice?.kind === 'credit_note' ? null : linkedInvoice?.invoice_status,
    paymentStatus: appointment.payment_status,
    punchCardHeld: Boolean(punchUsage) || appointment.payment_method === 'punch_card',
    paymentForfeited: Boolean(appointment.payment_forfeited_at),
  });
  const paymentChipLabel = t(visitPaymentStateLabelKey(visitPayment));
  const methodLabelKey = paymentMethodI18nKey(settlement.method);
  const PaymentIcon =
    settlement.method === 'punch_card'
      ? Ticket
      : settlement.method === 'card' || settlement.method === 'mobilepay'
        ? CreditCard
        : settlement.paid
          ? CheckCircle
          : AlertCircle;

  const statusColors: Record<string, string> = {
    upcoming: 'bg-ds-teal-tint text-ds-teal',
    checked_in: 'bg-ds-teal-tint text-ds-teal',
    under_treatment: 'bg-ds-coral-tint text-ds-coral-ink',
    treatment_finished: 'bg-ds-green-tint text-ds-green-ink',
    completed: 'bg-ds-green-tint text-ds-green-ink',
    cancelled: 'bg-ds-neutral-tint text-ds-muted',
    no_show: 'bg-ds-coral-tint text-ds-coral-ink',
  };

  const applyLaterOverride = async (choice: CancellationOverrideChoice) => {
    if (!overrideReason.trim()) {
      toast.error(t('appointments.override.reason_required'));
      return;
    }
    const { error } = await supabase.rpc('override_cancellation', {
      p_appointment_id: appointmentId,
      p_choice: choice,
      p_reason: overrideReason.trim(),
      p_amount_cents:
        choice === 'partial_refund'
          ? Math.round(Number(overrideAmount.replace(',', '.')) * 100)
          : undefined,
    });
    if (error) {
      toast.error(t('appointments.override.failed'));
      return;
    }
    toast.success(t('appointments.override.history'));
    setShowLaterOverride(false);
  };

  const handleDelete = async () => {
    if (allowOverride && overrideChoice !== 'keep_policy' && !overrideReason.trim()) {
      toast.error(t('appointments.override.reason_required'));
      return;
    }
    try {
      setIsDeleting(true);
      await cancelAppointment.mutateAsync({
        appointment_id: appointmentId,
        reason: cancelReason.trim() || 'Deleted by user',
      });
      if (wherebyEnabled && isVideoAppointment(appointment)) {
        await syncVideo.mutateAsync({ appointmentId, reason: 'cancelled' }).catch(() => undefined);
      }
      const optedOut = Boolean(patient?.appointment_email_opt_out);
      const hasEmail = Boolean(patient?.email?.trim());
      if (sendCancelEmail && !optedOut && hasEmail) {
        sendStaffAppointmentEmail(appointmentId, 'cancelled');
      }
      if (allowOverride && overrideChoice !== 'keep_policy') {
        const { error: overrideError } = await supabase.rpc('override_cancellation', {
          p_appointment_id: appointmentId,
          p_choice: overrideChoice,
          p_reason: overrideReason.trim(),
          p_amount_cents:
            overrideChoice === 'partial_refund'
              ? Math.round(Number(overrideAmount.replace(',', '.')) * 100)
              : undefined,
        });
        if (overrideError) {
          toast.error(t('appointments.override.failed'));
        }
      }
      toast.success(t('appointments.toasts.cancelled'));
      setShowCancelConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error(t('appointments.errors.unknown', 'Der opstod en fejl'));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGoToPatient = () => {
    if (!hasPatient) return;
    navigate(`/patients/${appointment.patient_id}`);
    onClose();
  };

  const handleOpenJournal = () => {
    const fromDay =
      location.pathname === '/appointments' || location.pathname.startsWith('/appointments/');
    const path = appointmentJournalWritePath(appointment, {
      source: fromDay ? 'day' : 'appointment',
      from: `${location.pathname}${location.search}`,
    });
    if (!path) return;
    navigate(path);
    onClose();
  };

  const handleMarkNoShow = async () => {
    if (!confirm(t('appointments.modal.confirm_no_show'))) return;
    try {
      await markNoShow.mutateAsync(appointmentId);
      toast.success(t('appointments.toasts.no_show'));
    } catch (err) {
      console.error('mark no-show failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  };

  const handleFinish = async () => {
    setFinishChoice(false);
    try {
      const result = await finishVisit.mutateAsync(appointmentId);
      toast.success(t(finishToastKey(result.outcome as FinishOutcome)));
    } catch (err) {
      console.error('finish appointment failed', err);
      toast.error(t('appointments.finish.failed'));
    }
  };

  const handleSetStatus = async (status: VisitStatus) => {
    try {
      await setVisitStatus.mutateAsync({
        appointment_id: appointmentId,
        visit_status: status,
      });
      toast.success(t('appointments.visit_status.status_updated'));
    } catch {
      toast.error(t('appointments.visit_status.status_update_failed'));
    }
  };

  const openPaymentPanel = () => {
    setShowChangePayment(false);
    setShowPaymentPanel(true);
  };

  const runPaymentAction = async (action: PaymentPrimaryAction | PaymentMoreAction) => {
    switch (action) {
      case 'finish_and_invoice':
      case 'finish_and_use_clip':
        await handleFinish();
        return;
      case 'record_payment':
      case 'take_prepayment':
      case 'pay_instead':
        openPaymentPanel();
        return;
      case 'change_payment_method':
        setShowPaymentPanel(false);
        setShowChangePayment(true);
        return;
      case 'mark_no_show':
        await handleMarkNoShow();
        return;
      case 'open_invoice':
      case 'credit':
      case 'refund':
        if (invoiceId) handleOpenInvoice(invoiceId);
        return;
      case 'resend_invoice':
        if (!invoiceId) return;
        try {
          await sendInvoiceEmail.mutateAsync({ invoice_id: invoiceId });
          toast.success(t('appointments.payment_choice.invoice_sent'));
        } catch (err) {
          console.error('resend invoice failed', err);
          toast.error(t('appointments.payment_choice.errors.email_failed'));
        }
        return;
      case 'undo_clip':
        if (!punchUsage?.id || !appointment.patient_id) return;
        try {
          await revertClip.mutateAsync({
            usage_id: punchUsage.id,
            patient_id: appointment.patient_id,
            clinic_id: clinicId,
          });
          toast.success(t('appointments.payment_section.undo_clip'));
        } catch (err) {
          console.error('undo clip failed', err);
          toast.error(t('appointments.errors.unknown'));
        }
        return;
      case 'none':
        return;
      default: {
        const _exhaustive: never = action;
        return _exhaustive;
      }
    }
  };

  const handleOpenInvoice = (invoiceId: string) => {
    navigate(
      buildAppointmentInvoiceHref({
        invoiceId,
        patientId: appointment.patient_id ?? '',
        clinicId,
        appointmentId,
        returnTo: appointment.patient_id ? `/patients/${appointment.patient_id}` : '/',
      }),
    );
    onClose();
  };

  const handleUseClip = async () => {
    const card = pickPreferredPunchCard(applicablePunchCards);
    if (!card || !appointment.patient_id) return;
    try {
      if (payableInvoice?.invoice_id && phase !== 'unfinished') {
        await staffPayment.replaceInvoiceWithKlip.mutateAsync({
          punchCardId: card.id,
          appointmentId,
          patientId: appointment.patient_id,
          clinicId,
        });
        toast.success(t('appointments.finish.done_punch'));
        setFinishChoice(false);
        return;
      }
      await staffPayment.finishWithClip.mutateAsync({
        punchCardId: card.id,
        appointmentId,
        patientId: appointment.patient_id,
        clinicId,
      });
      setFinishChoice(false);
      toast.success(t('appointments.finish.done_punch'));
    } catch (err) {
      console.error('use clip failed', err);
      toast.error(t('appointments.payment_choice.errors.klip_failed'));
    }
  };

  const handleRemind = async () => {
    if (!payableInvoice?.invoice_id) return;
    try {
      await sendReminder.mutateAsync({ invoice_id: payableInvoice.invoice_id });
      toast.success(t('invoices.reminder_sent_toast'));
    } catch (err) {
      console.error('reminder failed', err);
      toast.error(t('invoices.reminder_failed_toast'));
    }
  };

  const runMore = (action: BetalingMoreAction) => {
    switch (action) {
      case 'credit':
      case 'refund':
        if (payableInvoice?.invoice_id) handleOpenInvoice(payableInvoice.invoice_id);
        return;
      case 'record_payment':
        openPaymentPanel();
        return;
      case 'use_clip':
        void handleUseClip();
        return;
      case 'override':
        setShowLaterOverride(true);
        return;
      case 'resend_invoice':
        void runPaymentAction('resend_invoice');
        return;
      default: {
        const unexpected: never = action;
        return unexpected;
      }
    }
  };

  const moreLabel = (action: BetalingMoreAction): string => {
    switch (action) {
      case 'credit':
        return 'appointments.payment_section.credit';
      case 'refund':
        return 'appointments.payment_section.refund';
      case 'record_payment':
        return 'invoices.record_payment';
      case 'use_clip':
        return 'appointments.payment_choice.use_clip';
      case 'override':
        return 'appointments.override.legend';
      case 'resend_invoice':
        return 'appointments.payment_section.resend_invoice';
      default: {
        const unexpected: never = action;
        return unexpected;
      }
    }
  };

  const vs = appointment.visit_status as VisitStatus | null;
  const currentIdx = vs ? VISIT_STEPS.findIndex((step) => step.key === vs) : -1;
  const canEdit = Boolean(onEdit) && canStaffRescheduleAppointment(appointment.status);

  const patientContact = [
    patient?.email,
    formatPatientTelephoneNumbers(patient?.telephone_subscribers, patient?.phone),
  ]
    .filter(Boolean)
    .join(' · ');
  const invoiceId = settlement.invoiceId;
  const paymentPanelOpen =
    showPaymentPanel &&
    (settlement.canRecordPayment ||
      (settlement.canStartPayment && settlementAllowsNewPayment(settlement)));
  const changePaymentOpen = showChangePayment && settlement.canChangePaymentMethod;
  const payableInvoice = linkedInvoice?.kind === 'credit_note' ? null : linkedInvoice;
  const phase = appointmentBetalingPhase({
    appointmentStatus: appointment.status,
    invoiceStatus: payableInvoice?.invoice_status,
    visitPayment,
    totalCents: payableInvoice?.total_cents,
    paidCents: payableInvoice?.total_paid_cents,
  });
  const applicablePunchCards = selectApplicablePunchCards(activePunchCards ?? [], appointment.service_id);
  const canUseClip = applicablePunchCards.length > 0 && !punchUsage;
  const betalingPrimaryAction = betalingPrimary(phase, Boolean(payableInvoice));
  const showFinishButton =
    betalingPrimaryAction === 'finish' &&
    canFinishAndInvoice({ status: appointment.status, hasPatient, canCreateInvoices });
  const moreActions = betalingMore({
    phase,
    hasInvoice: Boolean(payableInvoice),
    allowOverride:
      allowOverride && (appointment.status === 'cancelled' || appointment.status === 'no_show'),
    canUseClip,
  });
  const sendMenuPlace = betalingShowsSendMenu(
    phase,
    Boolean(payableInvoice) &&
      canOfferPaymentRequest({
        outstandingCents: Math.max(
          0,
          (payableInvoice?.total_cents ?? 0) - (payableInvoice?.total_paid_cents ?? 0),
        ),
        status: payableInvoice?.invoice_status ?? '',
      }),
  );
  const showInvoiceLink = betalingShowsInvoiceLink(phase, Boolean(payableInvoice?.invoice_id));
  const creditNoteNumber = creditNotes?.[0]?.invoice_number ?? null;
  const paidOn = danishPaymentDate(payableInvoice?.paid_at ?? payableInvoice?.created_at);
  const recordedMethodKeys = displayPaymentMethods([
    ...[...(recordedPayments ?? [])]
      .sort((left, right) => (left.payment_date ?? '').localeCompare(right.payment_date ?? ''))
      .map((payment) => payment.payment_method),
    settlement.method,
  ]);
  const methodName =
    recordedMethodKeys.length > 0
      ? recordedMethodKeys
          .map((method) => {
            const key = paymentMethodI18nKey(method);
            return key ? t(key, method) : method;
          })
          .join(' + ')
      : methodLabelKey
        ? t(methodLabelKey, settlement.method ?? '')
        : settlement.method;
  const paymentSummary = betalingSummary({
    phase,
    visitPayment,
    invoiceNumber: payableInvoice?.invoice_number ?? null,
    method: methodName,
    paidOn,
    creditNoteNumber,
    unpaid: t('appointments.payment.state_unpaid'),
    prepaid: t('appointments.payment.state_prepaid'),
    unpaidInvoice: (number) => t('appointments.payment.line_unpaid_invoice', { number }),
    partialInvoice: (number) => t('appointments.payment.line_partial_invoice', { number }),
    overdue: t('appointments.payment.forfalden'),
    paid: (method, date) => t('appointments.payment.line_paid', { method, date }),
    refunded: (number) =>
      number
        ? t('appointments.payment.line_refunded', { number })
        : t('appointments.payment.line_refunded_plain'),
  });

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-hidden">
        <div
          className="fixed inset-0 bg-ds-scrim transition-opacity"
          onClick={onClose}
          aria-hidden="true"
        />

        <div className={DETAILS_SHELL} data-appointment-details-anchor="top">
          <div
            data-appointment-details-preview=""
            className={DETAILS_CARD}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="shrink-0 border-b border-ds-line px-4 py-2.5 md:rounded-t-2xl">
              <div className="flex items-center justify-between gap-3">
                <h2 className={DETAILS_TITLE}>{t('appointments.details.title')}</h2>
                <Button
                  type="button"
                  variant="tertiary"
                  size="icon"
                  onClick={onClose}
                  aria-label={t('common.close')}
                >
                  <X />
                </Button>
              </div>
            </div>

            <div data-appointment-details-body="" className="min-h-0 overflow-y-auto px-4 py-3">
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3 rounded-lg border border-ds-teal-line bg-ds-teal-tint px-3 py-2">
                  <div className="min-w-0">
                    <h3 className={cn('truncate', PATIENT_NAME)}>{patientName}</h3>
                    {patientContact ? (
                      <p className={cn('truncate', SECONDARY_TEXT)}>{patientContact}</p>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5',
                        STATUS_BADGE,
                        statusColors[lifecycleStatus] || statusColors.upcoming,
                      )}
                    >
                      {t(appointmentStatusLabelKey(lifecycleStatus))}
                    </span>
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5',
                        STATUS_BADGE,
                        visitPayment === 'paid' || visitPayment === 'prepaid'
                          ? 'bg-ds-green-tint text-ds-green-ink'
                          : visitPayment === 'partly_paid'
                            ? 'bg-ds-amber-tint text-ds-amber-ink'
                            : 'bg-ds-neutral-tint text-ds-muted',
                      )}
                    >
                      {paymentChipLabel}
                    </span>
                    <PreVisitStatusBadge
                      assignmentId={preVisitAssignmentId}
                      status={preVisitStatus}
                    />
                  </div>
                </div>
                <section>
                  <div className="mb-2 flex items-center gap-1.5">
                    <Calendar className="h-4 w-4 text-ds-muted" />
                    <h4 className={SECTION_HEADING}>{t('appointments.details.info_section')}</h4>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
                    <div>
                      <dt className={FIELD_LABEL}>{t('appointments.details.date')}</dt>
                      <dd className={FIELD_VALUE}>{formatCopenhagenDate(appointment.starts_at)}</dd>
                    </div>
                    <div>
                      <dt className={FIELD_LABEL}>{t('appointments.details.time')}</dt>
                      <dd className={FIELD_VALUE}>{formatCopenhagenTime(appointment.starts_at)}</dd>
                    </div>
                    <div>
                      <dt className={FIELD_LABEL}>{t('appointments.details.duration')}</dt>
                      <dd className={FIELD_VALUE}>
                        {t('appointments.details.duration_minutes', { count: durationMinutes })}
                      </dd>
                    </div>
                    {appointment.service_name && (
                      <div>
                        <dt className={FIELD_LABEL}>{t('appointments.details.treatment')}</dt>
                        <dd className={cn('truncate', FIELD_VALUE)}>{appointment.service_name}</dd>
                      </div>
                    )}
                    {appointment.course_badge ? (
                      <div>
                        <dt className={FIELD_LABEL}>{t('appointments.details.course')}</dt>
                        <dd className={FIELD_VALUE}>
                          {t('appointments.course_badge', {
                            n: appointment.course_badge.index,
                            total: appointment.course_badge.total,
                          })}
                        </dd>
                      </div>
                    ) : null}
                    {appointment.clinician_name && (
                      <div>
                        <dt className={FIELD_LABEL}>{t('appointments.details.practitioner')}</dt>
                        <dd className={cn('truncate', FIELD_VALUE)}>
                          {appointment.clinician_name}
                        </dd>
                      </div>
                    )}
                    {resourceLabel && (
                      <div>
                        <dt className={FIELD_LABEL}>{t('appointments.details.resource')}</dt>
                        <dd className={cn('truncate', FIELD_VALUE)}>{resourceLabel}</dd>
                      </div>
                    )}
                    {clinic?.name && (
                      <div>
                        <dt className={FIELD_LABEL}>{t('appointments.details.clinic')}</dt>
                        <dd className={cn('truncate', FIELD_VALUE)}>{clinic.name}</dd>
                      </div>
                    )}
                    {appointment.source && (
                      <div>
                        <dt className={FIELD_LABEL}>{t('appointments.details.source')}</dt>
                        <dd className={cn('truncate', FIELD_VALUE)}>
                          {t(`appointments.details.source_${appointment.source}`, appointment.source)}
                        </dd>
                      </div>
                    )}
                  </dl>
                </section>

                {hasPatient && (
                  <section
                    className="min-h-[4.75rem] rounded-lg border border-ds-line px-3 py-2"
                    data-betaling-phase={phase}
                  >
                    <div className="flex min-h-10 flex-wrap items-center justify-between gap-2">
                      <div className="min-w-0">
                        <h4 className={SECTION_HEADING}>{t('appointments.payment.label')}</h4>
                        <div className="mt-0.5 flex flex-col items-start gap-1">
                          <p className={cn('inline-flex items-center gap-1', FIELD_VALUE)}>
                            <PaymentIcon className="h-3.5 w-3.5 shrink-0" />
                            <span>{paymentSummary}</span>
                          </p>
                          {showInvoiceLink && payableInvoice?.invoice_id && canViewInvoices ? (
                            <button
                              type="button"
                              className="text-sm font-semibold text-ds-teal"
                              onClick={() => handleOpenInvoice(payableInvoice.invoice_id)}
                            >
                              {t('appointments.details.see_invoice')}
                            </button>
                          ) : null}
                        </div>
                      </div>
                      <div className="flex min-h-10 flex-wrap items-center justify-end gap-2">
                        {showFinishButton ? (
                          <Button
                            type="button"
                            disabled={finishVisit.isPending}
                            onClick={() => {
                              if (canUseClip) setFinishChoice(true);
                              else void handleFinish();
                            }}
                          >
                            {t('appointments.finish.action')}
                          </Button>
                        ) : null}
                        {betalingPrimaryAction === 'record' &&
                        (settlement.canRecordPayment || settlement.canStartPayment) ? (
                          <Button
                            type="button"
                            aria-expanded={paymentPanelOpen}
                            onClick={() => {
                              if (paymentPanelOpen) setShowPaymentPanel(false);
                              else openPaymentPanel();
                            }}
                          >
                            {t('invoices.record_payment')}
                          </Button>
                        ) : null}
                        {betalingPrimaryAction === 'remind' && payableInvoice?.invoice_id ? (
                          <Button
                            type="button"
                            disabled={sendReminder.isPending}
                            onClick={() => void handleRemind()}
                          >
                            {t('invoices.send_reminder')}
                          </Button>
                        ) : null}
                        {sendMenuPlace === 'beside' && payableInvoice?.invoice_id ? (
                          <InvoiceSendMenu clinicId={clinicId} invoiceId={payableInvoice.invoice_id} />
                        ) : null}
                        {moreActions.length > 0 || sendMenuPlace === 'more' ? (
                          <MenuPopover
                            align="start"
                            trigger={
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 text-sm text-ds-muted"
                                data-appointment-payment-more=""
                              >
                                {t('appointments.override.more')}
                                <ChevronDown className="h-4 w-4" aria-hidden />
                              </button>
                            }
                          >
                            {sendMenuPlace === 'more' && payableInvoice?.invoice_id ? (
                              <InvoiceSendMenu
                                rowsOnly
                                clinicId={clinicId}
                                invoiceId={payableInvoice.invoice_id}
                              />
                            ) : null}
                            {moreActions.map((action) => (
                              <MenuItem key={action} onClick={() => runMore(action)}>
                                {t(moreLabel(action))}
                              </MenuItem>
                            ))}
                          </MenuPopover>
                        ) : null}
                      </div>
                    </div>
                    {finishChoice && phase === 'unfinished' && !payableInvoice?.invoice_id ? (
                      <div className="mt-2 flex flex-wrap gap-2" data-finish-choice="">
                        <p className="w-full text-sm text-ds-muted">{t('appointments.finish.choose')}</p>
                        <Button type="button" onClick={() => void handleUseClip()}>
                          {t('appointments.payment_choice.use_clip')}
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => void handleFinish()}>
                          {t('appointments.finish.invoice_only')}
                        </Button>
                      </div>
                    ) : null}
                    {showLaterOverride ? (
                      <div className="mt-2 space-y-2" data-cancellation-override="">
                        <p className="text-sm text-ds-muted">{t('appointments.override.legend')}</p>
                        <Input
                          id="later-override-reason"
                          label={t('appointments.override.reason')}
                          value={overrideReason}
                          onChange={(event) => setOverrideReason(event.target.value)}
                        />
                        <Input
                          id="later-override-amount"
                          label={t('appointments.override.amount')}
                          inputMode="decimal"
                          value={overrideAmount}
                          onChange={(event) => setOverrideAmount(event.target.value)}
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" variant="secondary" onClick={() => void applyLaterOverride('full_refund')}>
                            {t('appointments.override.full_refund')}
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => void applyLaterOverride('partial_refund')}>
                            {t('appointments.override.partial_refund')}
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => void applyLaterOverride('restore_clip')}>
                            {t('appointments.override.restore_clip')}
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => void applyLaterOverride('waive_fee')}>
                            {t('appointments.override.waive_fee')}
                          </Button>
                        </div>
                      </div>
                    ) : null}
                    {paymentPanelOpen && patient && (
                      <div
                        data-appointment-payment-panel=""
                        className="mt-3 border-t border-ds-line pt-3"
                      >
                        <PaymentDialog
                          clinicId={clinicId}
                          invoiceId={invoiceId}
                          onCancel={() => setShowPaymentPanel(false)}
                          appointment={{
                            clinicId,
                            appointmentId,
                            patient,
                            serviceId: appointment.service_id,
                            serviceName: appointment.service_name,
                            startsAt: appointment.starts_at,
                            priceCents,
                            currency: service?.currency ?? clinic?.default_currency ?? 'DKK',
                            embedded: true,
                            canRedeemPunchCard: canViewPunchCards,
                            canSettleInvoice: canViewInvoices && canCreateInvoices,
                            blockPunchCard: Boolean(invoiceId),
                            onFinished: () => setShowPaymentPanel(false),
                            onOpenInvoice: handleOpenInvoice,
                          }}
                        />
                      </div>
                    )}
                    {changePaymentOpen && (
                      <div className="mt-3 border-t border-ds-line pt-3">
                        <AppointmentChangePaymentPanel
                          clinicId={clinicId}
                          appointmentId={appointmentId}
                          patientId={appointment.patient_id!}
                          serviceId={appointment.service_id}
                          currentLabel={paymentSummary}
                          onFinished={() => setShowChangePayment(false)}
                        />
                      </div>
                    )}
                  </section>
                )}

                {(canAdvanceVisitWorkflow(lifecycleStatus) ||
                  lifecycleStatus === 'treatment_finished' ||
                  appointment.status === 'completed') && (
                  <section data-visit-status="">
                    <h4 className={cn('mb-2', SECTION_HEADING)}>
                      {t('appointments.details.status_section')}
                    </h4>
                    <div className="flex items-center gap-1">
                      {VISIT_STEPS.map((step, idx) => {
                        const isCurrent =
                          currentIdx === idx ||
                          (currentIdx < 0 &&
                            step.key === 'treatment_finished' &&
                            appointment.status === 'completed');
                        const isPast = currentIdx > idx;
                        const readOnly = !canAdvanceVisitWorkflow(lifecycleStatus);
                        const className = cn(
                          'flex-1 rounded-md border px-2 py-1.5 text-center leading-tight',
                          STATUS_BADGE,
                          isCurrent
                            ? `${step.activeColor} border-current`
                            : isPast
                              ? 'border-transparent bg-ds-neutral-tint text-ds-muted'
                              : `${step.color} border-transparent`,
                          !readOnly && !isCurrent && 'hover:opacity-80',
                        );
                        if (readOnly) {
                          return (
                            <div key={step.key} className={className} aria-current={isCurrent ? 'step' : undefined}>
                              {t(step.labelKey)}
                            </div>
                          );
                        }
                        return (
                          <button
                            key={step.key}
                            type="button"
                            disabled={setVisitStatus.isPending || finishVisit.isPending}
                            onClick={() => void handleSetStatus(step.key)}
                            title={t(step.labelKey)}
                            className={cn(className, 'transition-all')}
                          >
                            {t(step.labelKey)}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                )}

                {appointment.notes ? (
                  <section>
                    <div className="mb-1.5 flex items-center gap-1.5">
                      <MessageSquare className="h-4 w-4 text-ds-muted" />
                      <h4 className={SECTION_HEADING}>{t('appointments.details.notes')}</h4>
                    </div>
                    <div
                      className={cn(
                        'line-clamp-3 whitespace-pre-wrap rounded-md border border-ds-teal-line bg-ds-teal-tint px-2.5 py-1.5 text-ds-text-2',
                        TYPE.body,
                      )}
                    >
                      {appointment.notes}
                    </div>
                  </section>
                ) : null}

                {hasPatient && (
                  <ButtonGroup
                    data-appointment-details-areas=""
                    className="border-t border-ds-line pt-3"
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleOpenJournal}
                      title={
                        journalEntry
                          ? journalEntry.finalized_at
                            ? t('appointments.details.journal_finalized')
                            : t('appointments.details.journal_draft')
                          : undefined
                      }
                    >
                      <FileText />
                      {t('appointments.details.journal')}
                    </Button>
                    <AppointmentCourseButton
                      patientId={appointment.patient_id}
                      courseId={appointment.treatment_course_id}
                      stepId={appointment.treatment_course_step_id}
                      onOpen={(path) => {
                        navigate(path);
                        onClose();
                      }}
                    />
                    <Button type="button" variant="secondary" onClick={handleGoToPatient}>
                      <UserIcon />
                      {t('appointments.details.see_patient')}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => setShowMessageModal(true)}
                    >
                      <MessageSquare />
                      {t('appointments.details.message')}
                    </Button>
                  </ButtonGroup>
                )}
              </div>
            </div>

            <div className="shrink-0 border-t border-ds-line px-4 py-2.5 md:rounded-b-2xl">
              <div className="flex items-center justify-between gap-2">
                <Button
                  onClick={() => setShowCancelConfirm(true)}
                  disabled={isDeleting}
                  variant="danger"
                >
                  <Trash2 />
                  <span>
                    {isDeleting
                      ? t('appointments.details.deleting')
                      : t('appointments.details.delete_or_cancel')}
                  </span>
                </Button>
                <ButtonGroup>
                  {wherebyEnabled && isVideoAppointment(appointment) ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => {
                        void (async () => {
                          try {
                            const meeting = await joinVideo.mutateAsync(appointmentId);
                            if (meeting.hostUrl) openExternalMeeting(meeting.hostUrl);
                          } catch {
                            toast.error(t('appointments.video.join_error'));
                          }
                        })();
                      }}
                      loading={joinVideo.isPending}
                    >
                      <Video />
                      {t('appointments.video.join')}
                    </Button>
                  ) : null}
                  <Button type="button" variant="secondary" onClick={onClose}>
                    {t('common.close')}
                  </Button>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => {
                        onClose();
                        onEdit?.();
                      }}
                    >
                      <Edit />
                      {t('common.edit')}
                    </Button>
                  )}
                  {onEdit && !canEdit && (
                    <Button
                      type="button"
                      variant="secondary"
                      disabled
                      title={t('appointments.details.edit_only_upcoming')}
                    >
                      <Edit />
                      {t('common.edit')}
                    </Button>
                  )}
                </ButtonGroup>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showMessageModal && hasPatient && (
        <MessageModal
          patientId={appointment.patient_id!}
          patientName={patientName}
          clinicId={clinicId}
          onClose={() => setShowMessageModal(false)}
        />
      )}

      <CancelAppointmentModal
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => void handleDelete()}
        pending={isDeleting}
        patientName={patientName}
        serviceName={appointment.service_name}
        startsAt={appointment.starts_at}
        endsAt={appointment.ends_at}
        clinicianName={appointment.clinician_name}
        reason={cancelReason}
        onReasonChange={setCancelReason}
        sendEmail={sendCancelEmail}
        onSendEmailChange={setSendCancelEmail}
        allowOverride={allowOverride}
        overrideChoice={overrideChoice}
        onOverrideChoiceChange={setOverrideChoice}
        overrideReason={overrideReason}
        onOverrideReasonChange={setOverrideReason}
        overrideAmount={overrideAmount}
        onOverrideAmountChange={setOverrideAmount}
        sendEmailDisabledReason={
          patient?.appointment_email_opt_out
            ? t('appointments.modal.send_patient_email_opted_out')
            : !patient?.email?.trim()
              ? t('appointments.modal.send_patient_email_no_email')
              : null
        }
      />
    </>
  );
}
